import { handleFormatAmount } from "@/utility/helper";
import { CircleCheck, CircleMinus } from "lucide-react";


type BreakdownRow = {
  label: string;
  isFixed: string;
  amount: number;
  isConditionBased?: string;
};

const BreakdownTable: React.FC<{
  rows: BreakdownRow[];
  columnLabel: string;
  emptyText: string;
  totalLabel: string;
  totalColor: string;
}> = ({ rows, columnLabel, emptyText, totalLabel, totalColor }) => {
  if (!rows || rows.length === 0)
    return (
      <div className="py-8 text-center text-slate-400 text-sm">{emptyText}</div>
    );

  const total = rows.reduce((sum, r) => sum + (r.amount || 0), 0);
  const showConditionBased = rows.some(
    (r) =>
      r.isConditionBased !== undefined &&
      r.isConditionBased !== null &&
      r.isConditionBased !== ""
  );

  const headers = [
    columnLabel,
    "Fixed",
    ...(showConditionBased ? ["ConditionBased"] : []),
    "Amount (₹)",
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50/80 border-b border-slate-200">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className={`py-3 px-4 font-semibold text-slate-600 text-xs uppercase tracking-wider ${
                  h === "Amount (₹)" ? "text-right" : "text-left"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, idx) => (
            <tr
              key={idx}
              className="hover:bg-slate-50/70 transition-colors even:bg-slate-50/30"
            >
              <td className="py-3 px-4 font-medium text-slate-800">
                {row.label}
              </td>
              <td className="py-3 px-4 text-slate-600">
                {row.isFixed === "Yes" ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600">
                    <CircleCheck className="w-4 h-4" /> Yes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-slate-400">
                    <CircleMinus className="w-4 h-4" /> No
                  </span>
                )}
              </td>
              {showConditionBased && (
                <td className="py-3 px-4 text-slate-600">
                  {row.isConditionBased === "Yes" ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-600">
                      <CircleCheck className="w-4 h-4" /> Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-slate-400">
                      <CircleMinus className="w-4 h-4" /> No
                    </span>
                  )}
                </td>
              )}
              <td className="py-3 px-4 text-right font-medium text-slate-800">
                {handleFormatAmount(row.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bg-slate-50/80 border-t border-slate-200 px-4 py-3 flex justify-between items-center">
        <span className="text-sm font-medium text-slate-600">{totalLabel}</span>
        <span className={`text-sm font-bold ${totalColor}`}>
          {handleFormatAmount(total)}
        </span>
      </div>
    </div>
  );
};

export default BreakdownTable;
